const getBool = (value) => value === "true";

const getMode = () => {
  if (window.matchMedia("(max-width: 749px)").matches) return "mobile";
  if (window.matchMedia("(max-width: 989px)").matches) return "tablet";
  return "desktop";
};

const initSwipeRoot = (root) => {
  if (!(root instanceof HTMLElement)) return;
  if (root.dataset.swipeInitialized === "true") return;

  const content = root.querySelector("[data-swipe-content]");
  if (!content) return;

  root.dataset.swipeInitialized = "true";

  const dotsHost = root.querySelector("[data-swipe-dots]");
  const counterCurrent = root.querySelector(".js-carousel-counter-current");
  const counterTotal = root.querySelector(".js-carousel-counter-total");
  const isPdpGallery = root.dataset.swipeRoot === "pdp-gallery";
  const showDotsGlobal = getBool(root.dataset.showDots);
  const showDotsMobile = getBool(root.dataset.showDotsMobile);
  const showDotsTablet = getBool(root.dataset.showDotsTablet);
  const showDotsDesktop = getBool(root.dataset.showDotsDesktop);
  const anyModeDots = showDotsMobile || showDotsTablet || showDotsDesktop;
  const showDots = dotsHost && (showDotsGlobal || anyModeDots);
  const arrowsHost = root.querySelector("[data-swipe-arrows]");
  const showArrows = getBool(root.dataset.showArrows) && arrowsHost;

  const swipeMobile = getBool(root.dataset.swipemobile ?? root.dataset.swipeMobile);
  const swipeTablet = getBool(root.dataset.swipetablet ?? root.dataset.swipeTablet);
  const swipeDesktop = getBool(root.dataset.swipedesktop ?? root.dataset.swipeDesktop);
  const snapEnabled = getBool(root.dataset.snap);

  const auto = getBool(root.dataset.autoScroll);
  const infinite = getBool(root.dataset.infinite);
  const infiniteSwipe = getBool(root.dataset.infiniteSwipe);
  const interval = parseFloat(root.dataset.autoInterval || "0");

  let timer = null;
  let scrollWrapTimer = null;
  let dots = [];

  const isModeActive = (mode) => {
    if (mode === "mobile") return swipeMobile;
    if (mode === "tablet") return swipeTablet;
    return swipeDesktop;
  };

  const isSnapEnabled = (mode) => {
    if (!snapEnabled) return false;
    if (mode === "mobile") return getBool(root.dataset.snapMobile);
    if (mode === "tablet") return getBool(root.dataset.snapTablet);
    return getBool(root.dataset.snapDesktop);
  };

  const isDotsEnabled = (mode) => {
    if (!showDots) return false;
    if (anyModeDots) {
      if (mode === "mobile") return showDotsMobile;
      if (mode === "tablet") return showDotsTablet;
      return showDotsDesktop;
    }
    return showDotsGlobal;
  };

  const isArrowsEnabled = (mode) => {
    if (!showArrows) return false;
    if (mode === "mobile") return swipeMobile;
    if (mode === "tablet") return swipeTablet;
    return swipeDesktop;
  };

  const getArrowStep = (mode) => {
    if (mode === "mobile") return parseInt(root.dataset.arrowStepMobile || "1", 10);
    if (mode === "tablet") return parseInt(root.dataset.arrowStepTablet || "1", 10);
    return parseInt(root.dataset.arrowStepDesktop || "1", 10);
  };

  const isVisibleItem = (item) => {
    if (!(item instanceof HTMLElement)) return false;
    const styles = window.getComputedStyle(item);
    if (styles.display === "none" || styles.visibility === "hidden") return false;
    if (item.offsetParent === null) return false;
    return item.offsetWidth > 0 && item.offsetHeight > 0;
  };

  const getItems = (mode) => {
    let selector = root.dataset.swipeItems;
    if (mode === "mobile" && root.dataset.swipeItemsMobile) selector = root.dataset.swipeItemsMobile;
    if (mode === "tablet" && root.dataset.swipeItemsTablet) selector = root.dataset.swipeItemsTablet;
    if (mode === "desktop" && root.dataset.swipeItemsDesktop) selector = root.dataset.swipeItemsDesktop;
    const items = selector ? Array.from(content.querySelectorAll(selector)) : Array.from(content.children);
    // Filter out hidden/zero-sized items to keep dots aligned with visible slides.
    return items.filter(isVisibleItem);
  };

  const getSlidesPerView = (mode) => {
    if (mode === "mobile") return parseInt(root.dataset.slidesPerViewMobile || "0", 10) || 0;
    if (mode === "tablet") return parseInt(root.dataset.slidesPerViewTablet || "0", 10) || 0;
    return parseInt(root.dataset.slidesPerViewDesktop || "0", 10) || 0;
  };

  const getTargetLeft = (index, modeForTarget) => {
    const itemsForMode = getItems(modeForTarget);
    const perViewForMode = getSlidesPerView(modeForTarget);
    const targetIndex = perViewForMode > 0 ? index * perViewForMode : index;
    const targetItem = itemsForMode[targetIndex];
    return targetItem ? targetItem.offsetLeft : 0;
  };

  const getSlideMetrics = (mode) => {
    const items = getItems(mode);
    const first = items[0];
    if (!first) return { slide: 0, gap: 0, step: 0, max: 0 };

    const parent = first.parentElement || content;
    const parentStyles = getComputedStyle(parent);
    let gap =
      parseFloat(parentStyles.columnGap || parentStyles.gap || parentStyles.rowGap || "0") || 0;

    if (!gap && items[1]) {
      const rect1 = first.getBoundingClientRect();
      const rect2 = items[1].getBoundingClientRect();
      gap = Math.max(0, rect2.left - rect1.right);
    }

    const slide = first.getBoundingClientRect().width;
    const step = slide + gap;
    const max = content.scrollWidth - content.clientWidth;
    return { slide, gap, step, max };
  };

  let lastActiveIndex = -1;

  const playActiveMedia = (items, activeIndex, perView) => {
    if (!isPdpGallery) return;
    if (!items.length) return;
    const itemIndex = perView > 0 ? activeIndex * perView : activeIndex;
    const activeItem = items[Math.min(Math.max(itemIndex, 0), items.length - 1)];
    if (!activeItem) return;

    // Pause any other playing media in this swipe group.
    content.querySelectorAll(".js-media-item-video").forEach((video) => {
      if (video !== activeItem.querySelector(".js-media-item-video") && !video.paused) {
        video.pause();
      }
    });
    content.querySelectorAll(".js-media-item-youtube").forEach((frame) => {
      if (activeItem.contains(frame)) return;
      frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
    });
    content.querySelectorAll(".js-media-item-vimeo").forEach((frame) => {
      if (activeItem.contains(frame)) return;
      frame.contentWindow?.postMessage('{"method":"pause"}', "*");
    });

    const deferred = activeItem.querySelector(".js-product-media-deferred-video");
    if (deferred && typeof deferred.loadContent === "function") {
      deferred.loadContent();
    }

    const activeVideo = activeItem.querySelector(".js-media-item-video");
    if (activeVideo && activeVideo.play) {
      activeVideo.play().catch(() => {});
    }
  };

  const updateCounter = () => {
    if (!counterCurrent && !counterTotal) return;
    const mode = getMode();
    if (!isModeActive(mode)) return;

    const items = getItems(mode);
    const perView = getSlidesPerView(mode);
    const { step } = getSlideMetrics(mode);
    const max = content.scrollWidth - content.clientWidth;
    const total =
      perView > 0
        ? Math.max(1, Math.ceil(items.length / perView))
        : step > 0
          ? Math.max(1, Math.floor(max / step) + 1)
          : 1;

    let activeIndex = 0;
    if (items.length) {
      let closestIndex = 0;
      let closestDelta = Infinity;
      items.forEach((item, index) => {
        const delta = Math.abs(item.offsetLeft - content.scrollLeft);
        if (delta < closestDelta) {
          closestDelta = delta;
          closestIndex = index;
        }
      });
      activeIndex = perView > 0 ? Math.floor(closestIndex / perView) : closestIndex;
    } else if (step > 0) {
      const distance = perView > 0 ? step * perView : step;
      activeIndex = Math.round(content.scrollLeft / distance);
    }
    activeIndex = Math.min(Math.max(activeIndex, 0), Math.max(0, total - 1));

    if (counterCurrent) counterCurrent.textContent = String(activeIndex + 1);
    if (counterTotal) counterTotal.textContent = String(total);

    if (activeIndex !== lastActiveIndex) {
      lastActiveIndex = activeIndex;
      playActiveMedia(items, activeIndex, perView);
    }
  };

  const updateDots = () => {
    const mode = getMode();
    if (!dotsHost) return;
    if (!isDotsEnabled(mode) || !isModeActive(mode)) {
      dotsHost.innerHTML = "";
      dots = [];
      return;
    }

    const items = getItems(mode);
    const perView = getSlidesPerView(mode);
    const { step } = getSlideMetrics(mode);
    const max = content.scrollWidth - content.clientWidth;
    const total =
      perView > 0
        ? Math.max(1, Math.ceil(items.length / perView))
        : step > 0
          ? Math.max(1, Math.floor(max / step) + 1)
          : 1;

    if (dots.length !== total) {
      dotsHost.innerHTML = "";
      dots = Array.from({ length: total }, (_, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = dotsHost.dataset.dotClass || "group-block__dot";
        button.addEventListener("click", () => {
          const modeForTarget = getMode();
          const snap = isSnapEnabled(modeForTarget);
          const targetLeft = getTargetLeft(index, modeForTarget);
          content.scrollTo({ left: targetLeft, behavior: snap ? "smooth" : "auto" });
        });
        dotsHost.appendChild(button);
        return button;
      });
    }

    let activeIndex = 0;
    if (items.length) {
      let closestIndex = 0;
      let closestDelta = Infinity;
      items.forEach((item, index) => {
        const delta = Math.abs(item.offsetLeft - content.scrollLeft);
        if (delta < closestDelta) {
          closestDelta = delta;
          closestIndex = index;
        }
      });
      activeIndex = perView > 0 ? Math.floor(closestIndex / perView) : closestIndex;
    } else if (step > 0) {
      const distance = perView > 0 ? step * perView : step;
      activeIndex = Math.round(content.scrollLeft / distance);
    }
    activeIndex = Math.min(Math.max(activeIndex, 0), Math.max(0, dots.length - 1));
    dots.forEach((dot, index) => {
      dot.classList.toggle("is-active", index === activeIndex);
    });
  };

  const start = () => {
    stop();
    if (!auto || Number.isNaN(interval) || interval <= 0) return;
    if (!isModeActive(getMode())) return;
    timer = setInterval(() => {
      const { step, max } = getSlideMetrics(getMode());
      if (!step) return;
      let next = content.scrollLeft + step;
      if (next > max - 2) {
        if (infinite) {
          content.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          content.scrollTo({ left: max, behavior: "smooth" });
        }
      } else {
        content.scrollTo({ left: next, behavior: "smooth" });
      }
    }, interval * 1000);
  };

  const stop = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };

  const updateSnap = () => {
    const mode = getMode();
    root.dataset.snap = isSnapEnabled(mode) ? "true" : "false";
  };

  const updateArrows = () => {
    if (!showArrows || !arrowsHost) return;
    const mode = getMode();
    const enabled = isArrowsEnabled(mode);
    arrowsHost.hidden = !enabled;
    if (!enabled) return;

    const prev = arrowsHost.querySelector(".group-block__arrow--prev");
    const next = arrowsHost.querySelector(".group-block__arrow--next");
    const { max } = getSlideMetrics(getMode());
    if (prev) prev.disabled = !infiniteSwipe && content.scrollLeft <= 1;
    if (next) next.disabled = !infiniteSwipe && content.scrollLeft >= max - 1;
  };

  const bindArrows = () => {
    if (!showArrows || !arrowsHost) return;
    const prev = arrowsHost.querySelector(".group-block__arrow--prev");
    const next = arrowsHost.querySelector(".group-block__arrow--next");

    if (prev) {
      prev.addEventListener("click", () => {
        const mode = getMode();
        if (!isArrowsEnabled(mode)) return;
        const { step } = getSlideMetrics(getMode());
        const amount = step * getArrowStep(mode);
        let target = content.scrollLeft - amount;
        if (infiniteSwipe && target <= 1) {
          const { max } = getSlideMetrics(getMode());
          target = max;
        } else {
          target = Math.max(0, target);
        }
        content.scrollTo({ left: target, behavior: "smooth" });
      });
    }

    if (next) {
      next.addEventListener("click", () => {
        const mode = getMode();
        if (!isArrowsEnabled(mode)) return;
        const { step, max } = getSlideMetrics(getMode());
        const amount = step * getArrowStep(mode);
        let target = content.scrollLeft + amount;
        if (infiniteSwipe && target >= max - 1) {
          target = 0;
        } else {
          target = Math.min(max, target);
        }
        content.scrollTo({ left: target, behavior: "smooth" });
      });
    }
  };

  const scheduleWrap = () => {
    if (!infiniteSwipe) return;
    if (!isModeActive(getMode())) return;
    if (scrollWrapTimer) clearTimeout(scrollWrapTimer);
    scrollWrapTimer = setTimeout(() => {
      const { max } = getSlideMetrics(getMode());
      if (max <= 0) return;
      if (content.scrollLeft >= max - 1) {
        content.scrollTo({ left: 0, behavior: "auto" });
      } else if (content.scrollLeft <= 1) {
        content.scrollTo({ left: max, behavior: "auto" });
      }
    }, 120);
  };

  const onScroll = () => {
    window.requestAnimationFrame(() => {
      updateDots();
      updateCounter();
      updateArrows();
    });
    scheduleWrap();
  };

  content.addEventListener("scroll", onScroll, { passive: true });
  root.addEventListener("mouseenter", stop);
  root.addEventListener("mouseleave", start);
  root.addEventListener("touchstart", stop, { passive: true });
  root.addEventListener("touchend", start, { passive: true });

  window.addEventListener("resize", () => {
    updateSnap();
    updateDots();
    updateCounter();
    updateArrows();
    start();
  });

  updateSnap();
  content.scrollTo({ left: getTargetLeft(0, getMode()), behavior: "auto" });
  bindArrows();
  updateDots();
  updateCounter();
  updateArrows();
  start();
};

const initSwipeRoots = (scope = document) => {
  scope.querySelectorAll("[data-swipe-root]").forEach(initSwipeRoot);
};

initSwipeRoots();

document.addEventListener("shopify:section:load", (event) => {
  if (event.target instanceof HTMLElement) {
    initSwipeRoots(event.target);
  }
});
